import { useState, useRef, useEffect } from "react";
import API from "../utils/API";
import ReCAPTCHA from "react-google-recaptcha";
import './registerModal.css';

export default function RegisterModal() {
    const recaptcha = useRef();

    const [registration, setRegistration] = useState({ first_name: "", last_name: "", password: "", email: "" });
    const [passwordInput, setPasswordInput] = useState("");
    const [betaEmails, setBetaEmails] = useState([]);
    const [approvedEmails, setApprovedEmails] = useState([]);
    const [allowedEmails, setAllowedEmails] = useState([]);
    const [approvedDomains, setApprovedDomains] = useState([]);
    const [betaConfirm, setBetaConfirm] = useState(false);
    const [loading, setLoading] = useState(false);

    const [upperVerify, setUpperVerify] = useState(false);
    const [lowerVerify, setLowerVerify] = useState(false);
    const [numberVerify, setNumberVerify] = useState(false);
    const [symbolVerify, setSymbolVerify] = useState(false);
    const [lengthVerify, setLengthVerify] = useState(false);

    useEffect(() => {
        API.fetchBetaEmails().then(d => setBetaEmails(d.data)).catch(() => {});
        API.fetchApprovedEmails().then(d => setApprovedEmails(d.data)).catch(() => {});
        API.fetchAllowedEmails().then(d => setAllowedEmails(d.data)).catch(() => {});
        API.fetchApprovedDomains().then(d => setApprovedDomains(d.data)).catch(() => {});
    }, []);

    const onChange = (e) => {
        const { name, value } = e.target;
        setRegistration(prev => ({ ...prev, [name]: value }));

        if (name === "email") {
            const domain = value.split('@')[1];
            setBetaConfirm(
                betaEmails.some(r => r.beta_email === value) ||
                approvedEmails.some(r => r.work_email === value) ||
                allowedEmails.some(r => r.approved_email === value) ||
                /@.*\.ca\.gov$/.test(value) ||
                approvedDomains.some(r => r.approved_domain === domain)
            );
        }
    };

    const validatePassword = (e) => {
        const val = e.target.value;
        setPasswordInput(val);
        const u = /[A-Z]/.test(val);
        const l = /[a-z]/.test(val);
        const n = /[0-9]/.test(val);
        const s = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(val);
        const len = val.length >= 10;
        setUpperVerify(u);
        setLowerVerify(l);
        setNumberVerify(n);
        setSymbolVerify(s);
        setLengthVerify(len);
        if (u && l && n && s && len) setRegistration(prev => ({ ...prev, password: val }));
    };

    const resetStates = () => {
        setRegistration({ first_name: "", last_name: "", password: "", email: "" });
        setPasswordInput("");
        setUpperVerify(false);
        setLowerVerify(false);
        setNumberVerify(false);
        setSymbolVerify(false);
        setLengthVerify(false);
        setBetaConfirm(false);
    };

    const submitRegistration = async () => {
        const captchaValue = recaptcha.current.getValue();
        if (!captchaValue) { alert('Please verify the reCAPTCHA!'); return; }

        const data = await API.captchaVerify(JSON.stringify({ captchaValue }));
        if (!data.data.success) { alert('reCAPTCHA validation failed!'); return; }

        try {
            setLoading(true);
            const referral_token = localStorage.getItem('eg_ambassador_token') || undefined;
            await API.signup({ ...registration, referral_token });
            resetStates();
            alert('Registration successful! Please check your email to confirm your account.\n\nSome providers may filter the confirmation — check your junk folder if you don\'t see it within a few minutes.');
        } catch {
            alert("Registration failed. Please try again.");
            resetStates();
        } finally {
            setLoading(false);
        }
    };

    const checkIcon = (ok) => `bi bi-${ok ? 'check2-circle' : 'exclamation-circle'}`;

    return (
        <>
            <div className="modal fade rm-modal" id="registerModal" tabIndex="-1" aria-labelledby="registerModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">

                        <div className="modal-header">
                            <div className="rm-header-text">
                                <h5 className="rm-title" id="registerModalLabel">
                                    Join <i>'ello</i>Govna
                                </h5>
                                <p className="rm-subtitle">Create your free account</p>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
                        </div>

                        <div className="rm-body">
                            <p className="rm-intro">
                                Registration is open to government affairs professionals working in or around
                                Sacramento. If you have a <strong>ca.gov email</strong> or an email address
                                listed on <strong>Cal-Access</strong>, you're eligible for a free account.
                            </p>

                            <div className="rm-row">
                                <div className="rm-field">
                                    <label className="rm-label" htmlFor="rm-first">First Name</label>
                                    <input id="rm-first" className="rm-input" type="text" name="first_name" value={registration.first_name} onChange={onChange} />
                                </div>
                                <div className="rm-field">
                                    <label className="rm-label" htmlFor="rm-last">Last Name</label>
                                    <input id="rm-last" className="rm-input" type="text" name="last_name" value={registration.last_name} onChange={onChange} />
                                </div>
                            </div>

                            <div className="rm-row">
                                <div className="rm-field">
                                    <label className="rm-label" htmlFor="rm-email">Email</label>
                                    <input id="rm-email" className="rm-input" type="email" name="email" value={registration.email} onChange={onChange} />
                                </div>
                                <div className="rm-field">
                                    <label className="rm-label" htmlFor="rm-password">Password</label>
                                    <input id="rm-password" className="rm-input" type="password" name="password" value={passwordInput} onChange={validatePassword} />
                                </div>
                            </div>

                            <div className="rm-pw-req">
                                <span className={lengthVerify ? 'met' : ''}><i className={checkIcon(lengthVerify)} /> 10+ characters</span>
                                <span className={upperVerify ? 'met' : ''}><i className={checkIcon(upperVerify)} /> Uppercase</span>
                                <span className={lowerVerify ? 'met' : ''}><i className={checkIcon(lowerVerify)} /> Lowercase</span>
                                <span className={numberVerify ? 'met' : ''}><i className={checkIcon(numberVerify)} /> Number</span>
                                <span className={symbolVerify ? 'met' : ''}><i className={checkIcon(symbolVerify)} /> Symbol</span>
                            </div>
                        </div>

                        <div className="rm-footer">
                            <div className="rm-captcha">
                                <ReCAPTCHA ref={recaptcha} sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY} />
                            </div>
                            <div className="rm-actions">
                                <button className="rm-btn rm-btn--close" data-bs-dismiss="modal">
                                    Close
                                </button>
                                <button
                                    className="rm-btn rm-btn--register"
                                    data-bs-dismiss="modal"
                                    onClick={submitRegistration}
                                    disabled={!betaConfirm}
                                >
                                    {betaConfirm ? 'Register' : 'Access Unavailable'}
                                </button>
                            </div>
                            <p className="rm-note">
                                Best practices encourage a unique password for each account. We recommend
                                checking yours against the{' '}
                                <a href="https://haveibeenpwned.com/Passwords" target="_blank" rel="noreferrer">
                                    Have I Been Pwned
                                </a>{' '}
                                database of known breaches to ensure you're not using a compromised password.
                            </p>
                        </div>

                    </div>
                </div>
            </div>

            {loading && (
                <div className="d-flex position-fixed top-50 start-50 translate-middle">
                    <div className="spinner-border text-secondary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}
        </>
    );
}
